import React, { useEffect, useState } from 'react';
import {
  Card, Table, Button, Tag, Typography, Space, Alert, Empty,
  Popconfirm, Modal, Form, Input, Select, message, Row, Col, Segmented, theme as antdTheme,
} from 'antd';
import {
  PlusOutlined, DeleteOutlined, StopOutlined, SearchOutlined,
  MailOutlined, WarningOutlined, CloseCircleOutlined, UserDeleteOutlined,
} from '@ant-design/icons';
import axios from 'axios';

const { Title, Text } = Typography;
const { Option } = Select;

const TYPE_COLOR = {
  bounce:      'error',
  complaint:   'warning',
  unsubscribe: 'default',
};

const Suppression = () => {
  const { token: themeToken } = antdTheme.useToken();
  const [suppressions, setSuppressions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [addModal, setAddModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const fetchSuppressions = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get('/api/suppression');
      setSuppressions(res.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load suppression list');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSuppressions(); }, []);

  const handleAdd = async (values) => {
    setSaving(true);
    try {
      await axios.post('/api/suppression', values);
      form.resetFields();
      setAddModal(false);
      message.success('Email added to suppression list');
      fetchSuppressions();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add suppression');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`/api/suppression/${id}`);
      message.success('Removed from suppression list');
      fetchSuppressions();
    } catch (err) {
      message.error(err.response?.data?.message || 'Failed to remove from suppression list');
    }
  };

  const counts = {
    total:       suppressions.length,
    bounce:      suppressions.filter(s => s.type === 'bounce').length,
    complaint:   suppressions.filter(s => s.type === 'complaint').length,
    unsubscribe: suppressions.filter(s => s.type === 'unsubscribe').length,
  };

  const filtered = suppressions.filter(s =>
    (typeFilter === 'all' || s.type === typeFilter) &&
    (s.email.toLowerCase().includes(search.toLowerCase()) ||
     (s.reason || '').toLowerCase().includes(search.toLowerCase()))
  );

  const StatCard = ({ label, value, icon, color, bg }) => (
    <Card style={{ borderRadius: 14, border: `1px solid ${themeToken.colorBorderSecondary}`, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }} bodyStyle={{ padding: 18 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ width: 40, height: 40, borderRadius: 10, background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {React.cloneElement(icon, { style: { color, fontSize: 18 } })}
        </div>
        <div>
          <Text style={{ display: 'block', fontSize: 12, color: themeToken.colorTextSecondary }}>{label}</Text>
          <Text style={{ fontSize: 22, fontWeight: 700, lineHeight: 1.1 }}>{value.toLocaleString()}</Text>
        </div>
      </div>
    </Card>
  );

  const columns = [
    {
      title: 'Email', dataIndex: 'email',
      render: email => <Text strong style={{ fontFamily: 'monospace', fontSize: 13 }}>{email}</Text>,
    },
    {
      title: 'Type', dataIndex: 'type', width: 130,
      render: t => (
        <Tag color={TYPE_COLOR[t] || 'default'} style={{ fontWeight: 500, textTransform: 'capitalize' }}>
          {t}
        </Tag>
      ),
    },
    { title: 'Reason', dataIndex: 'reason', render: r => r || '—' },
    {
      title: 'Added', dataIndex: 'created_at', width: 160,
      render: d => <Text style={{ fontSize: 12, color: themeToken.colorTextSecondary }}>{new Date(d).toLocaleString()}</Text>,
    },
    {
      title: '', align: 'right', width: 80,
      render: (_, record) => (
        <Popconfirm
          title="Remove from suppression list?"
          description="This email will be eligible to receive future campaigns."
          onConfirm={() => handleDelete(record.id)}
          okText="Remove"
          okButtonProps={{ danger: true }}
        >
          <Button danger icon={<DeleteOutlined />} size="small">Remove</Button>
        </Popconfirm>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <Title level={3} style={{ margin: 0 }}>Suppression List</Title>
          <Text style={{ color: themeToken.colorTextSecondary }}>
            {suppressions.length} suppressed email{suppressions.length !== 1 ? 's' : ''} — these addresses won't receive campaigns
          </Text>
        </div>
        <Button type="primary" icon={<PlusOutlined />} size="large" onClick={() => setAddModal(true)} style={{ borderRadius: 10, fontWeight: 600 }}>
          Add Email
        </Button>
      </div>

      {error && <Alert message={error} type="error" showIcon closable onClose={() => setError('')} style={{ marginBottom: 16, borderRadius: 10 }} />}

      <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
        <Col xs={12} md={6}><StatCard label="Total Suppressed" value={counts.total}       icon={<MailOutlined />}       color="#6366f1" bg="#ede9fe" /></Col>
        <Col xs={12} md={6}><StatCard label="Bounced"          value={counts.bounce}      icon={<WarningOutlined />}    color="#f59e0b" bg="#fef3c7" /></Col>
        <Col xs={12} md={6}><StatCard label="Complaints"       value={counts.complaint}   icon={<CloseCircleOutlined />} color="#ef4444" bg="#fef2f2" /></Col>
        <Col xs={12} md={6}><StatCard label="Unsubscribed"     value={counts.unsubscribe} icon={<UserDeleteOutlined />} color="#6b7280" bg="#f3f4f6" /></Col>
      </Row>

      <Card style={{ borderRadius: 14, border: `1px solid ${themeToken.colorBorderSecondary}`, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
        <div style={{ marginBottom: 16, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <Input
            placeholder="Search by email or reason…"
            prefix={<SearchOutlined style={{ color: themeToken.colorTextTertiary }} />}
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ maxWidth: 320, borderRadius: 10, flex: '1 1 220px' }}
            allowClear
          />
          <Segmented
            value={typeFilter}
            onChange={setTypeFilter}
            options={[
              { label: `All (${counts.total})`,             value: 'all' },
              { label: `Bounced (${counts.bounce})`,        value: 'bounce' },
              { label: `Complaints (${counts.complaint})`,  value: 'complaint' },
              { label: `Unsub (${counts.unsubscribe})`,     value: 'unsubscribe' },
            ]}
          />
        </div>
        <Table
          dataSource={filtered}
          columns={columns}
          rowKey="id"
          loading={loading}
          locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No suppressed emails — great deliverability!" /> }}
          pagination={{ pageSize: 15, showTotal: t => `${t} record${t !== 1 ? 's' : ''}` }}
        />
      </Card>

      <Modal
        title={
          <Space>
            <div style={{ width: 32, height: 32, background: '#fef2f2', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <StopOutlined style={{ color: '#ef4444' }} />
            </div>
            <span>Add to Suppression List</span>
          </Space>
        }
        open={addModal}
        onCancel={() => { setAddModal(false); form.resetFields(); }}
        footer={null}
        destroyOnHidden
        width={440}
      >
        <Form form={form} onFinish={handleAdd} layout="vertical" style={{ marginTop: 20 }}>
          <Form.Item name="email" label="Email Address" rules={[{ required: true, type: 'email', message: 'Enter a valid email' }]}>
            <Input placeholder="email@example.com" size="large" style={{ borderRadius: 10 }} />
          </Form.Item>
          <Form.Item name="type" label="Suppression Type" rules={[{ required: true, message: 'Select a type' }]}>
            <Select placeholder="Select type" size="large">
              <Option value="unsubscribe">Unsubscribe</Option>
              <Option value="bounce">Bounce</Option>
              <Option value="complaint">Complaint</Option>
            </Select>
          </Form.Item>
          <Form.Item name="reason" label="Reason (optional)">
            <Input placeholder="e.g., User requested removal" style={{ borderRadius: 10 }} />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={() => { setAddModal(false); form.resetFields(); }}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={saving} danger style={{ borderRadius: 8 }}>
                Add to Suppression
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Suppression;
